import { moveItemUp, moveItemDown } from '../../utils/orderUtils';
import type MultiSelect from '../index';

declare const Sortable: {
    new (element: HTMLElement, options: {
        handle?: string;
        animation?: number;
        onEnd?: () => void;
    }): { destroy: () => void };
};

export class ColumnsLayout {
    private ms: MultiSelect;
    private sortableInstance: { destroy: () => void } | null = null;

    constructor(multiSelect: MultiSelect) {
        this.ms = multiSelect;
    }

    private createElement<K extends keyof HTMLElementTagNameMap>(
        tag: K,
        className?: string,
        html: string = ''
    ): HTMLElementTagNameMap[K] {
        const el = document.createElement(tag);

        if (className) {
            el.className = className;
        }

        if (html) {
            el.innerHTML = html;
        }

        return el;
    }

    private createColumnItem(
        value: string,
        label: string,
        index: number,
        forSelectedList: boolean = false,
        group: string = ''
    ): HTMLDivElement {
        const { ms } = this;
        const item = this.createElement('div', 'column-item');
        item.dataset.value = value;
        item.dataset.order = String(index);

        if (group) {
            item.dataset.group = group;
        }

        if (ms.isSortable && forSelectedList) {
            item.innerHTML = `
                <span class="sortable-handle me-2">
                    <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor" style="pointer-events: none;">
                        <circle cx="2" cy="2" r="1.5"/>
                        <circle cx="6" cy="2" r="1.5"/>
                        <circle cx="2" cy="7" r="1.5"/>
                        <circle cx="6" cy="7" r="1.5"/>
                        <circle cx="2" cy="12" r="1.5"/>
                        <circle cx="6" cy="12" r="1.5"/>
                    </svg>
                </span>
                <span class="item-label">${label}</span>
                <div class="sort-arrows">
                    <button type="button" class="sort-arrow js-order-up" title="Move up" aria-label="Move up">
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor" style="pointer-events: none;">
                            <path d="M5 0L10 6H0L5 0Z"/>
                        </svg>
                    </button>
                    <button type="button" class="sort-arrow js-order-down" title="Move down" aria-label="Move down">
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor" style="pointer-events: none;">
                            <path d="M5 6L0 0H10L5 6Z"/>
                        </svg>
                    </button>
                </div>
            `;
        } else {
            item.innerHTML = `<span class="item-label">${label}</span>`;
        }

        return item;
    }

    public init(): void {
        const { ms } = this;

        ms.wrapper.classList.add('columns-layout');

        if (ms.isSortable) {
            ms.wrapper.classList.add('sortable');
        }

        if (ms.isSearchable) {
            const searchContainer = ms.search.createColumnsSearchContainer();
            ms.wrapper.appendChild(searchContainer);
            ms.search.initFromContainer(searchContainer);
        }

        ms.columnsContainer = this.createElement('div', 'columns-container');
        ms.availableColumn = this.createElement('div', 'column available-column');
        ms.selectedColumn = this.createElement('div', 'column selected-column');

        ms.availableColumn.appendChild(this.createElement('div', 'column-header', ms.availableLabel));

        const maxInfo = ms.maxSelected
            ? `<span class="max-selected-info">(<span class="selected-count">0</span>/${ms.maxSelected})</span>`
            : '';
        ms.selectedColumn.appendChild(this.createElement('div', 'column-header', `${ms.selectedLabel} ${maxInfo}`));

        const countSpan = ms.selectedColumn.querySelector<HTMLElement>('.selected-count');
        if (countSpan) {
            ms.maxSelectedFeature.setCountSpan(countSpan);
        }

        ms.availableList = this.createElement('div', 'column-list available-list');
        ms.selectedList = this.createElement('div', 'column-list selected-list');

        const optgroups = ms.select.querySelectorAll('optgroup');
        let optionIndex = 0;

        if (optgroups.length > 0) {
            optgroups.forEach((optgroup) => {
                const groupLabel = optgroup.label;
                const groupHeader = this.createElement('div', 'optgroup-header', groupLabel);
                groupHeader.dataset.group = groupLabel;
                ms.availableList.appendChild(groupHeader);

                Array.from(optgroup.querySelectorAll('option')).forEach((option) => {
                    if (option.value) {
                        const item = this.createColumnItem(option.value, option.text, optionIndex++, false, groupLabel);
                        ms.availableList.appendChild(item);
                    }
                });
            });

            // Handle any direct (ungrouped) options sitting outside any optgroup
            Array.from(ms.select.children).forEach((child) => {
                if (child.tagName === 'OPTION') {
                    const option = child as HTMLOptionElement;

                    if (option.value) {
                        const item = this.createColumnItem(option.value, option.text, optionIndex++, false);
                        ms.availableList.appendChild(item);
                    }
                }
            });
        } else {
            Array.from(ms.select.options).forEach((option, index) => {
                if (option.value) {
                    const item = this.createColumnItem(option.value, option.text, index, false);
                    ms.availableList.appendChild(item);
                }
            });
        }

        ms.availableColumn.appendChild(ms.availableList);
        ms.selectedColumn.appendChild(ms.selectedList);
        ms.columnsContainer.appendChild(ms.availableColumn);
        ms.columnsContainer.appendChild(ms.selectedColumn);
        ms.wrapper.appendChild(ms.columnsContainer);
        ms.wrapper.appendChild(ms.maxSelectedFeature.createErrorElement());

        // Initialize required dummy elements
        ms.toggle = document.createElement('div');
        ms.displayText = document.createElement('span');
        ms.menu = document.createElement('div');
        ms.menuBody = document.createElement('div');
        ms.tagsContainer = document.createElement('div');
    }

    public renderMenu(): void {
        // No-op for this layout
    }

    public bindEvents(): void {
        const { ms } = this;

        ms.availableList.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const item = target.closest('.column-item') as HTMLElement | null;

            if (!item) {
                return;
            }

            if (target.closest('.sort-arrows') || target.closest('.sortable-handle')) {
                return;
            }

            if (!ms.maxSelectedFeature.canSelectMore()) {
                ms.maxSelectedFeature.showError();
                
                return;
            }

            const value = item.dataset.value;
            const option = ms.select.querySelector<HTMLOptionElement>(`option[value="${value}"]`);
            
            if (option) {
                option.selected = true;
                ms.updateSelection();
            }
        });

        ms.selectedList.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const item = target.closest('.column-item') as HTMLElement | null;

            if (!item) {
                return;
            }

            if (target.closest('.sort-arrows') || target.closest('.sortable-handle')) {
                return;
            }

            const value = item.dataset.value;
            const option = ms.select.querySelector<HTMLOptionElement>(`option[value="${value}"]`);
            
            if (option) {
                option.selected = false;
                ms.updateSelection();
            }
        });

        if (ms.isSearchable) {
            ms.search.bindColumnsEvents();
        }

        if (ms.isSortable) {
            this.bindSortEvents();
        }
    }

    private bindSortEvents(): void {
        const { ms } = this;
        const selectedList = ms.selectedList;

        selectedList.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const upBtn = target.closest('.js-order-up');
            const downBtn = target.closest('.js-order-down');

            if (!upBtn && !downBtn) {
                return;
            }

            e.stopPropagation();
            const item = (upBtn || downBtn) as HTMLElement;
            const columnItem = item.closest('.column-item') as HTMLElement | null;

            if (!columnItem) {
                return;
            }

            const moved = upBtn ? moveItemUp(columnItem) : moveItemDown(columnItem);
            
            if (moved) {
                this.syncSelectionOrder();
            }
        });

        if (typeof Sortable !== 'undefined') {
            this.sortableInstance = new Sortable(selectedList, {
                handle: '.sortable-handle',
                animation: 150,
                onEnd: () => {
                    this.syncSelectionOrder();
                }
            });
        }
    }

    private syncSelectionOrder(): void {
        const { ms } = this;
        const orderedValues = this.getOrderedSelectedValues();

        ms.wrapper.dataset.selectedOrder = JSON.stringify(orderedValues);

        if (ms.updateHiddenInputs) {
            ms.updateHiddenInputs(orderedValues);
        }

        ms.select.dispatchEvent(new CustomEvent('orderChange', {
            bubbles: true,
            detail: { orderedValues }
        }));

        if (ms.options.onOrderChange) {
            ms.options.onOrderChange(orderedValues, ms);
        }
    }

    public getOrderedSelectedValues(): string[] {
        const items = this.ms.selectedList.querySelectorAll<HTMLElement>('.column-item');
        
        return Array.from(items).map((item) => {
            return item.dataset.value || '';
        });
    }

    public updateSelection(): void {
        const { ms } = this;
        const selectedValues = Array.from(ms.select.selectedOptions).map((opt) => opt.value);
        let orderedValues = selectedValues;

        if (ms.isSortable && ms.select.dataset.selectedOrder && ms.selectedList.children.length === 0) {
            try {
                const initialOrder = JSON.parse(ms.select.dataset.selectedOrder).map(String);
                orderedValues = initialOrder.filter((v: string) => selectedValues.includes(v));

                selectedValues.forEach((v) => {
                    if (!orderedValues.includes(v)) {
                        orderedValues.push(v);
                    }
        });
            } catch {
                // Fall back to default order
            }
        }

        orderedValues.forEach((value) => {
            const item = ms.availableList.querySelector<HTMLElement>(`.column-item[data-value="${value}"]`);

            if (item) {
                if (ms.isSortable) {
                    const label = item.querySelector('.item-label')?.textContent || item.textContent || '';
                    const newItem = this.createColumnItem(
                        value,
                        label,
                        parseInt(item.dataset.order || '0', 10),
                        true,
                        item.dataset.group || ''
                    );
                    ms.selectedList.appendChild(newItem);
                    item.remove();
                } else {
                    ms.selectedList.appendChild(item);
                }
            }
        });

        ms.selectedList.querySelectorAll<HTMLElement>('.column-item').forEach((item) => {
            const value = item.dataset.value || '';

            if (!selectedValues.includes(value)) {
                const itemOrder = parseInt(item.dataset.order || '0', 10);
                const itemGroup = item.dataset.group || '';
                let newItem: HTMLElement = item;

                if (ms.isSortable) {
                    const label = item.querySelector('.item-label')?.textContent || item.textContent || '';
                    newItem = this.createColumnItem(value, label, itemOrder, false, itemGroup);
                    item.remove();
                }

                this.reinsertIntoAvailable(newItem, itemOrder, itemGroup);
            }
        });

        ms.maxSelectedFeature.updateCount(selectedValues.length);

        if (ms.isSortable) {
            this.syncSelectionOrder();
        }
    }

    /**
     * Returns the column item to the available list at the position that
     * matches its original `data-order`, scoped to its optgroup boundary so
     * grouped items land back under the right header instead of falling
     * through into the next group.
     */
    private reinsertIntoAvailable(newItem: HTMLElement, itemOrder: number, itemGroup: string): void {
        const { ms } = this;

        if (itemGroup) {
            const groupHeader = ms.availableList.querySelector<HTMLElement>(
                `.optgroup-header[data-group="${itemGroup}"]`
            );

            if (groupHeader) {
                let cursor: Element | null = groupHeader.nextElementSibling;

                while (cursor && !cursor.classList.contains('optgroup-header')) {
                    if (cursor.classList.contains('column-item')) {
                        const currentOrder = parseInt((cursor as HTMLElement).dataset.order || '0', 10);

                        if (currentOrder > itemOrder) {
                            ms.availableList.insertBefore(newItem, cursor);
                            return;
                        }
                    }

                    cursor = cursor.nextElementSibling;
                }

                // End of group — insert before the next optgroup-header, or append
                if (cursor) {
                    ms.availableList.insertBefore(newItem, cursor);
                } else {
                    ms.availableList.appendChild(newItem);
                }

                return;
            }
        }

        // Ungrouped item — fall back to flat order
        const availableItems = Array.from(ms.availableList.querySelectorAll<HTMLElement>('.column-item'));

        for (const availableEl of availableItems) {
            const currentOrder = parseInt(availableEl.dataset.order || '0', 10);

            if (currentOrder > itemOrder) {
                ms.availableList.insertBefore(newItem, availableEl);
                return;
            }
        }

        ms.availableList.appendChild(newItem);
    }

    public destroy(): void {
        if (this.sortableInstance) {
            this.sortableInstance.destroy();
        }
    }
}